<template>
  <form method="post" @submit.prevent="onSend" novalidate>
    <div class="modal-card" style="width: auto">
      <header class="modal-card-head">
        <p class="modal-card-title is-centered">
          {{ formTitle }}
        </p>
        <b-button v-if="!isNew" type="is-danger" @click="onDelete">
          取消
        </b-button>
      </header>
      <section class="modal-card-body">
        <form-wrapper :validator="$v.form">
          <form-group
            horizontal
            name="title"
            attribute="attributes.name"
            :label="$t('attributes.name')"
          >
            <b-input
              v-model="form.title"
              name="title"
              maxlength="150"
              :has-counter="false"
              :placeholder="
                $t('message.please_input_', [$t('attributes.name')]) +
                  $t('message.word_limit', [150])
              "
            ></b-input>
          </form-group>
          <form-group
            horizontal
            name="type"
            attribute="attributes.meeting_type"
            :label="$t('attributes.meeting_type')"
          >
            <b-select
              v-model="form.type"
              name="type"
              expanded
              :disabled="!isNew"
            >
              <option :value="meeting_types.BRIEFING">
                {{ $t(`attributes.meeting_type_${meeting_types.BRIEFING}`) }}
              </option>
              <option :value="meeting_types.LESSON">
                {{ $t(`attributes.meeting_type_${meeting_types.LESSON}`) }}
              </option>
            </b-select>
          </form-group>
          <form-group
            v-if="form.type === meeting_types.BRIEFING"
            horizontal
            attribute="attributes.group_name"
            :label="$t('attributes.group_name')"
          >
            <span v-html="selectedGroupName" class="ml-2"></span>
          </form-group>
          <b-field
            v-if="form.type === meeting_types.BRIEFING"
            horizontal
            name="group_code"
            attribute="attributes.group_code"
            :label="$t('attributes.group_code')"
          >
            <form-group name="group_code" attribute="attributes.group_code">
              <b-autocomplete
                ref="group_code_input"
                v-model="form.group_code"
                name="group_code"
                field="code"
                :data="groups"
                :loading="isLoadingGroups"
                :placeholder="$t('message.please_input_group_code')"
                :open-on-focus="false"
                class="mb-1"
                @typing="loadGroups"
                @select="onSelectGroup"
              >
                <template #default="props">
                  <p>
                    {{ props.option.code }} -
                    <!-- eslint-disable-next-line-->
                    <span v-html="props.option.name"></span>
                  </p>
                </template>
                <template #empty>
                  {{
                    form.group_code
                      ? isLoadingGroups
                        ? $t('attributes.searching')
                        : $t('attributes.not_found')
                      : $t('message.please_input_group_code')
                  }}
                </template>
              </b-autocomplete>
            </form-group>
            <form-group
              name="leader"
              attribute="attributes.leader"
              :label="$t('attributes.leader')"
              label-position="on-border"
            >
              <b-input
                v-model="form.leader"
                name="leader"
                attribute="attributes.leader"
                :label="$t('attributes.leader')"
                maxlength="20"
                :has-counter="false"
                :placeholder="
                  $t('message.please_input_', [$t('attributes.leader')]) +
                    $t('message.word_limit', [20])
                "
              ></b-input>
            </form-group>
          </b-field>
          <b-field
            horizontal
            grouped
            :label="$t('attributes.meeting_datetime')"
          >
            <form-group name="date" attribute="attributes.meeting_datetime">
              <b-datepicker
                v-model="form.date"
                name="date"
                :min-date="minDate"
                trap-focus
                :first-day-of-week="1"
                :mobile-native="false"
                :date-formatter="formatDate"
                @input="onDateChanged"
              >
              </b-datepicker>
            </form-group>
            <form-group
              name="start_time"
              attribute="attributes.meeting_start_time"
            >
              <b-timepicker
                v-model="form.start_time"
                name="start_time"
                :placeholder="$t('attributes.meeting_start_time')"
                hour-format="24"
                :min-time="minTime"
                :max-time="maxTime"
                :incrementMinutes="5"
                :mobile-native="false"
                @input="onStartTimeChanged"
              >
              </b-timepicker>
            </form-group>
            <form-group name="end_time" attribute="attributes.meeting_end_time">
              <b-timepicker
                v-model="form.end_time"
                name="end_time"
                :placeholder="$t('attributes.meeting_end_time')"
                hour-format="24"
                :min-time="minTime"
                :max-time="maxTime"
                :incrementMinutes="5"
                :mobile-native="false"
                @input="onEndTimeChanged"
              />
            </form-group>
          </b-field>
          <form-group
            horizontal
            name="signup_deadline"
            attribute="$t('attributes.signup_deadline')"
            :label="$t('attributes.signup_deadline')"
          >
            <b-datepicker
              v-model="form.signup_deadline"
              name="signup_deadline"
              :placeholder="
                $t('message.please_input') + $t('attributes.signup_deadline')
              "
              :min-date="deadlineMinDate"
              :max-date="deadlineMaxDate"
              :mobile-native="false"
              :date-formatter="formatDate"
            >
            </b-datepicker>
          </form-group>
          <b-field
            horizontal
            grouped
            :label="
              $t('attributes.meeting_place') +
                '/' +
                $t('attributes.meeting_capacity')
            "
          >
            <form-group name="place_type" attribute="attributes.meeting_place">
              <b-select
                v-model="form.place_type"
                name="place_type"
                expanded
                :label="$t('attributes.meeting_room')"
                @input="checkTime"
              >
                <option :value="meeting_places.INNER">
                  {{ $t(`attributes.meeting_place_${meeting_places.INNER}`) }}
                </option>
                <option :value="meeting_places.OTHER">
                  {{ $t(`attributes.meeting_place_${meeting_places.OTHER}`) }}
                </option>
              </b-select>
            </form-group>
            <form-group
              name="capacity"
              attribute="attributes.meeting_capacity"
              :label="$t('attributes.meeting_capacity')"
              label-position="on-border"
            >
              <b-input
                v-model="form.capacity"
                name="capacity"
                type="number"
              ></b-input>
            </form-group>
          </b-field>
          <form-group
            v-if="form.place_type === meeting_places.OTHER"
            horizontal
            name="place"
            attribute="attributes.meeting_place"
            label=""
          >
            <b-input
              v-model="form.place"
              name="place"
              type="textarea"
              maxlength="500"
              :placeholder="
                $t('message.please_input_', [$t('attributes.meeting_place')])
              "
            ></b-input>
          </form-group>
          <form-group
            v-if="form.place_type === meeting_places.INNER"
            horizontal
            name="room_id"
            attribute="attributes.meeting_room"
            :label="$t('attributes.meeting_room')"
          >
            <b-select
              v-model="form.room_id"
              name="room_id"
              expanded
              @input="checkTime"
            >
              <option :value="null" disabled>
                {{
                  $t(`message.please_select_`, [$t('attributes.meeting_room')])
                }}
              </option>
              <option v-for="room in rooms" :key="room.id" :value="room.id">
                {{ room.name }}
              </option>
            </b-select>
          </form-group>
          <b-field
            horizontal
            grouped
            :label="$t('attributes.meeting_schedule_notify_type')"
          >
            <form-group
              name="notify_type"
              attribute="attributes.meeting_schedule_notify_type"
            >
              <b-select
                v-model="form.notify_type"
                name="notify_type"
                expanded
                disabled
              >
                <option :value="notify_types.NONE">
                  {{
                    $t(
                      `attributes.meeting_schedule_notify_type_${notify_types.NONE}`
                    )
                  }}
                </option>
                <option :value="notify_types.ALL">
                  {{
                    $t(
                      `attributes.meeting_schedule_notify_type_${notify_types.ALL}`
                    )
                  }}
                </option>
                <option :value="notify_types.SPECIFY">
                  {{
                    $t(
                      `attributes.meeting_schedule_notify_type_${notify_types.SPECIFY}`
                    )
                  }}
                </option>
              </b-select>
            </form-group>
            <form-group
              name="meal_provided"
              attribute="attributes.meal_provided"
              style="display: flex; justify-content: end; padding-top: 8px;"
            >
              <b-switch
                v-model="form.meal_provided"
                name="meal_provided"
                left-label
              >
                {{ $t('attributes.has_provide_meal') }}
              </b-switch>
            </form-group>
          </b-field>
          <form-group
            v-if="form.notify_type === notify_types.SPECIFY"
            horizontal
            name="notify_target"
            label="通知對象"
            attribute="attributes.meeting_schedule_notify_target"
          >
            <b-taginput
              v-model="notify_target"
              :loading="isLoadingEmployees"
              :data="employees"
              autocomplete
              field="name"
              placeholder="輸入員工編號或姓名找尋對象"
              :allow-duplicates="false"
              @typing="searchEmployees"
            >
              <template v-slot="props">
                [{{ props.option.emp_code }}] {{ props.option.name }}
              </template>
              <template #empty>
                未選擇
              </template>
            </b-taginput>
          </form-group>
          <b-field horizontal grouped :label="$t('attributes.lecturer')">
            <form-group name="lecturer" attribute="attributes.lecturer">
              <b-input
                v-model="form.lecturer"
                name="lecturer"
                attribute="attributes.lecturer"
                :label="$t('attributes.lecturer')"
                maxlength="20"
                :has-counter="false"
                :placeholder="
                  $t('message.please_input_', [$t('attributes.lecturer')]) +
                    $t('message.word_limit', [20])
                "
              ></b-input>
            </form-group>
            <form-group
              name="assistant"
              :label="$t('attributes.assistant')"
              attribute="attributes.assistant"
              label-position="on-border"
            >
              <b-input
                v-model="form.assistant"
                name="assistant"
                attribute="attributes.assistant"
                :label="$t('attributes.assistant')"
                maxlength="20"
                :has-counter="false"
                :placeholder="
                  $t('message.please_input_', [$t('attributes.assistant')]) +
                    $t('message.word_limit', [20])
                "
              ></b-input>
            </form-group>
            <form-group
              name="tel"
              attribute="attributes.meeting_tel"
              :label="$t('attributes.meeting_tel')"
              label-position="on-border"
            >
              <b-input
                v-model="form.tel"
                name="tel"
                attribute="attributes.meeting_tel"
                :label="$t('attributes.meeting_tel')"
                maxlength="20"
                :has-counter="false"
                :placeholder="
                  $t('message.please_input_', [$t('attributes.meeting_tel')]) +
                    $t('message.word_limit', [20])
                "
              ></b-input>
            </form-group>
          </b-field>
          <b-field horizontal grouped :label="$t('attributes.material_source')">
            <form-group name="material" attribute="attributes.material">
              <b-input
                v-model="form.material"
                name="material"
                attribute="attributes.material"
                :label="$t('attributes.material')"
                maxlength="50"
                :has-counter="false"
                :placeholder="
                  $t('message.please_input_', [
                    $t('attributes.material_source')
                  ]) + $t('message.word_limit', [50])
                "
              ></b-input>
            </form-group>
            <b-field class="file">
              <a class="button is-primary" @click="onSelectFile">
                <b-icon icon="upload"></b-icon>
                <span>{{ $t('message.please_select_file') }}</span>
              </a>
              <a
                v-if="form.material_file"
                :href="fantasyFileUrl(form.material_file)"
                target="_blank"
                class="nowrap"
                style="display: flex; align-items: center;"
              >
                <svg
                  style="margin-left: 6px; width: 36px; height: 36px; color: slategrey;"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="currentColor"
                    d="M14,2H6C4.89,2 4,2.89 4,4V20C4,21.11 4.89,22 6,22H18C19.11,22 20,21.11 20,20V8L14,2M12,19L8,15H10.5V12H13.5V15H16L12,19M13,9V3.5L18.5,9H13Z"
                  />
                </svg>
              </a>
            </b-field>
          </b-field>
          <form-group
            horizontal
            name="description"
            attribute="attributes.meeting_desc"
            :label="$t('attributes.meeting_desc')"
          >
            <b-input
              v-model="form.description"
              name="description"
              type="textarea"
              rows="4"
              maxlength="500"
              :placeholder="
                $t('message.please_input_', [$t('attributes.description')])
              "
            ></b-input>
          </form-group>
        </form-wrapper>
      </section>
      <footer class="modal-card-foot">
        <b-button class="is-fullwidth" @click="$parent.close()">
          {{ $t('attributes.close') }}
        </b-button>
        <b-button
          :loading="sending"
          :disabled="!canSend"
          type="is-primary"
          class="is-fullwidth"
          native-type="submit"
        >
          {{ isNew ? $t('attributes.booking') : $t('attributes.modify') }}
        </b-button>
      </footer>
    </div>

    <b-loading
      :active.sync="isLoading"
      :is-full-page="false"
      :can-cancel="false"
    ></b-loading>
  </form>
</template>

<script>
import {
  maxLength,
  minValue,
  numeric,
  required,
  requiredIf
} from 'vuelidate/lib/validators'
import {
  EMPLOYEES_API,
  MEETING_ROOMS_API,
  TRAVEL_GROUPS_API
} from '@/utils/apis'
import {
  MEETING_PLACE_TYPE,
  MEETING_SCHEDULE_NOTIFY_TYPE,
  MEETING_TYPE
} from '@/utils/constants'
import debounce from 'lodash/debounce'
import notifyService from '@/services/notifyService'
import { OpenFileFinder } from '@/utils/ckfinder'

export default {
  name: 'MeetingForm',
  props: {
    meeting: {
      type: Object,
      required: true
    },
    startHour: {
      type: Number,
      required: true
    },
    endHour: {
      type: Number,
      required: true
    },
    sending: {
      type: Boolean
    }
  },
  data() {
    const isNew = !this.meeting.id
    const formTitle = isNew
      ? this.$t('attributes.meeting_booking')
      : this.$t('attributes.meeting_booking_modify')

    const today = this.$dayjs().startOf('day')

    const min = new Date()
    min.setHours(this.startHour)
    min.setMinutes(0)
    const max = new Date()
    max.setHours(this.endHour)
    max.setMinutes(0)

    const deadlineMinDate = this.$dayjs()
      .startOf('day')
      .toDate()
    const deadlineMaxDate = this.$dayjs(this.meeting.start_time)
      .startOf('day')
      .toDate()

    return {
      isNew: isNew,
      formTitle: formTitle,

      loadingRooms: false,
      rooms: [],

      isLoadingGroups: false,
      groups: [],
      selectedGroupName: this.meeting.group_name,

      isLoadingEmployees: false,
      employees: [],

      minDate: today.toDate(),
      minTime: min,
      maxTime: max,

      deadlineMinDate: deadlineMinDate,
      deadlineMaxDate: deadlineMaxDate,

      meeting_types: MEETING_TYPE,
      meeting_places: MEETING_PLACE_TYPE,

      notify_types: MEETING_SCHEDULE_NOTIFY_TYPE,
      notify_target: [],
      form: this.extractForm(this.meeting)
    }
  },
  computed: {
    canSend() {
      const start = this.$dayjs(this.form.start_time)
      const end = this.$dayjs(this.form.end_time)
      return !start.isSame(end)
    },
    isLoading() {
      return this.loadingRooms || this.sending
    }
  },
  mounted() {
    this.loadRooms()

    if (
      this.meeting.notify === this.notify_types.SPECIFY &&
      this.meeting.notify_target
    ) {
      this.getEmployeesAsync(this.meeting.notify_target)
    }
  },
  validations() {
    return {
      form: {
        title: { required, maxLength: maxLength(150) },
        type: { required },
        group_code: {
          requiredIf: requiredIf(function(model) {
            return model.type === MEETING_TYPE.BRIEFING
          })
        },
        date: { required },
        start_time: { required },
        end_time: { required },
        signup_deadline: { required },
        place_type: { required },
        capacity: { required, numeric, minValue: minValue(0) },
        place: {
          requiredIf: requiredIf(function(model) {
            return model.place_type === MEETING_PLACE_TYPE.OTHER
          }),
          maxLength: maxLength(500)
        },
        room_id: {
          requiredIf: requiredIf(function(model) {
            return model.place_type === MEETING_PLACE_TYPE.INNER
          })
        },

        leader: {
          requiredIf: requiredIf(function(model) {
            return model.type === MEETING_TYPE.BRIEFING
          }),
          maxLength: maxLength(20)
        },
        lecturer: { required, maxLength: maxLength(20) },
        assistant: { maxLength: maxLength(20) },
        tel: { required },
        meal_provided: { required },
        material: { maxLength: maxLength(50) },
        description: { maxLength: maxLength(500) },

        notify_type: { required },
        notify_target: {
          requiredIf: requiredIf(function(model) {
            return model.notify_type === MEETING_SCHEDULE_NOTIFY_TYPE.SPECIFY
          })
        }
      }
    }
  },
  methods: {
    extractForm(meeting) {
      const date = this.$dayjs(meeting.start_time).startOf('day')
      const start_time = this.$dayjs(meeting.start_time).toDate()
      const end_time = this.$dayjs(meeting.end_time).toDate()
      const signup_deadline = this.$dayjs(meeting.signup_deadline).toDate()

      this.selectedGroupName = meeting.group_name

      return {
        id: meeting.id,
        title: meeting.title,
        type: meeting.type,
        group_code: meeting.group_code,
        date: date.toDate(),
        start_time: start_time,
        end_time: end_time,
        place_type: meeting.place_type,
        place: meeting.place,
        schedule_id: meeting.schedule_id,
        room_id: meeting.schedule ? meeting.schedule.room_id : null,
        capacity: meeting.capacity,
        leader: meeting.leader,
        lecturer: meeting.lecturer,
        assistant: meeting.assistant,
        tel: meeting.tel,
        signup_deadline: signup_deadline,
        meal_provided: meeting.meal_provided,
        material: meeting.material,
        material_file: meeting.material_file,
        description: meeting.description,

        notify_type: meeting.notify_type || MEETING_SCHEDULE_NOTIFY_TYPE.NONE,
        notify_target: meeting.notify_target || []
      }
    },
    async loadRooms() {
      try {
        const api = `${MEETING_ROOMS_API}`

        this.loadingRooms = true
        const res = await this.axios.get(api)
        this.rooms = res.data.data
      } catch (e) {
        this.rooms = []
        notifyService.fetch_error('meeting_room', e)
      } finally {
        this.loadingRooms = false
      }
    },
    async loadAsyncGroups() {
      const api = `${TRAVEL_GROUPS_API}/search`
      const params = {
        code: this.form.group_code,
        showExpired: true
      }

      this.isLoadingGroups = true
      try {
        const res = await this.axios.get(api, { params })
        this.groups = res.data.data
      } catch (e) {
        this.groups = []

        notifyService.fetch_error('group', e)
      } finally {
        this.isLoadingGroups = false
      }
    },
    loadGroups: debounce(function() {
      if (!this.form.group_code) {
        this.groups = []
        return
      }

      this.loadAsyncGroups()
    }, 1000),
    onSelectGroup(option) {
      this.form.group_code = option && option.code
      this.selectedGroupName = option && option.name
    },
    async getEmployeesAsync(list) {
      const api = `${EMPLOYEES_API}/list`
      const params = {
        codes: list.join(','),
        status: 1
      }

      this.isLoadingEmployees = true
      try {
        const res = await this.axios.get(api, { params })
        this.notify_target = res.data.data
      } catch (e) {
        this.notify_target = []

        notifyService.fetch_error('employee', e)
      } finally {
        this.isLoadingEmployees = false
      }
    },
    searchEmployees: debounce(function(search) {
      if (!search) {
        this.employees = []
        return
      }

      this.searchEmployeesAsync(search)
    }, 1000),
    async searchEmployeesAsync(search) {
      const api = `${EMPLOYEES_API}/search`
      const params = { search }

      this.isLoadingEmployees = true
      try {
        const res = await this.axios.get(api, { params })
        this.employees = res.data.data
      } catch (e) {
        this.employees = []

        notifyService.fetch_error('employee', e)
      } finally {
        this.isLoadingEmployees = false
      }
    },
    onDateChanged(date) {
      let maxDate = this.$dayjs(date).startOf('day')
      let deadline = this.$dayjs(this.form.signup_deadline).startOf('day')

      if (deadline.isAfter(maxDate)) {
        this.form.signup_deadline = maxDate.toDate()
      }

      this.deadlineMaxDate = maxDate.toDate()

      this.checkTime()
    },
    onStartTimeChanged(time) {
      const start = this.$dayjs(time)
      let end = this.$dayjs(this.form.end_time)

      const maxTime = this.$dayjs(time)
        .hour(this.endHour)
        .minute(0)

      if (start.isSameOrAfter(end)) {
        end = start.add(1, 'hour')

        if (end.isAfter(maxTime)) {
          end = maxTime
        }

        this.form.end_time = end.toDate()
      }

      this.checkTime()
    },
    onEndTimeChanged(time) {
      let start = this.$dayjs(this.form.start_time)
      let end = this.$dayjs(time)

      const minTime = this.$dayjs(time)
        .hour(this.startHour)
        .minute(0)
      const maxTime = this.$dayjs(time)
        .hour(this.endHour)
        .minute(0)

      if (end.isAfter(maxTime)) {
        end = maxTime
        this.form.end_time = end.toDate()
      }

      if (end.isSameOrBefore(start)) {
        start = end.subtract(1, 'hour')

        if (start.isBefore(minTime)) {
          start = minTime
        }

        this.form.start_time = start.toDate()
      }

      this.checkTime()
    },
    async onSelectFile() {
      try {
        this.form.material_file = await OpenFileFinder()
      } catch (e) {
        notifyService.error(e)
      }
    },
    async checkTime() {
      if (this.form.place_type !== MEETING_PLACE_TYPE.INNER) {
        return
      }
      if (!this.form.room_id) {
        return
      }

      const start = this.$dayjs(this.form.start_time)
      const end = this.$dayjs(this.form.end_time)
      const start_time = this.$dayjs(this.form.date)
        .startOf('day')
        .hour(start.hour())
        .minute(start.minute())
        .toDate()
      const end_time = this.$dayjs(this.form.date)
        .startOf('day')
        .hour(end.hour())
        .minute(end.minute())
        .toDate()

      const data = {
        schedule_id: this.form.schedule_id,
        start_time: start_time,
        end_time: end_time
      }

      this.$emit('onCheckTime', this.form.room_id, data)
    },
    async onSend() {
      const notify_target = this.notify_target.map(e => e.emp_code)
      this.form.notify_target = [...new Set(notify_target)]

      // eslint-disable-next-line no-console
      console.log('onSend', this.form)

      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }

      const start = this.$dayjs(this.form.start_time)
      const end = this.$dayjs(this.form.end_time)
      const start_time = this.$dayjs(this.form.date)
        .startOf('day')
        .hour(start.hour())
        .minute(start.minute())
        .toDate()
      const end_time = this.$dayjs(this.form.date)
        .startOf('day')
        .hour(end.hour())
        .minute(end.minute())
        .toDate()

      const data = {
        id: this.form.id,
        title: this.form.title,
        type: this.form.type,
        group_code:
          this.form.type === MEETING_TYPE.BRIEFING
            ? this.form.group_code
            : null,
        start_time: start_time,
        end_time: end_time,
        place_type: this.form.place_type,
        place:
          this.form.place_type === MEETING_PLACE_TYPE.OTHER
            ? this.form.place
            : null,
        room_id:
          this.form.place_type === MEETING_PLACE_TYPE.INNER
            ? this.form.room_id
            : null,
        capacity: this.form.capacity,
        leader:
          this.form.type === MEETING_TYPE.BRIEFING ? this.form.leader : null,
        lecturer: this.form.lecturer,
        assistant: this.form.assistant,
        tel: this.form.tel,
        signup_deadline: this.formatDate(this.form.signup_deadline),
        meal_provided: this.form.meal_provided,
        material: this.form.material,
        material_file: this.form.material_file,
        description: this.form.description,

        notify_type: this.form.notify_type,
        notify_target: this.form.notify_target.join(',')
      }

      // eslint-disable-next-line no-console
      console.log('onSend', data)

      this.$emit('onSend', data)
    },
    async onDelete() {
      this.$emit('onDelete', this.meeting)
    }
  }
}
</script>
